/**
 * Pins: a dot on one spot of one sheet (Fieldwire-style) carrying a type, title, status and
 * attachments (PDFs, images, phone photos). Stored per project, positioned in PDF page points
 * of the plan set's page (same space as markups), so they stay put at any zoom.
 */
export const PIN_TYPES = ["Submittal", "RFI", "Photo", "Note", "Punch", "Detail"] as const;
export type PinType = (typeof PIN_TYPES)[number];
export const PIN_STATUSES = ["open", "in progress", "closed"] as const;
export type PinStatus = (typeof PIN_STATUSES)[number];

/** Colour per type (pin dots and filter chips). */
export const PIN_COLORS: Record<PinType, string> = { Submittal: "#7c3aed", RFI: "#dc2626", Photo: "#0891b2", Note: "#ca8a04", Punch: "#ea580c", Detail: "#059669" };

export interface PinAttachment {
  id: string;
  key: string;
  name: string;
  contentType: string;
  size: number;
  addedAt: string;
  /** Set when the attachment is a daily report photo (blob owned by the report, not the pin). */
  source?: { reportId: string; photoId: string };
}

export interface Pin {
  id: string;
  projectId: string;
  documentId: string;
  pageId: string;
  /** PDF page points (unrotated page space). */
  x: number;
  y: number;
  type: PinType;
  title: string;
  status: PinStatus;
  description: string;
  attachments: PinAttachment[];
  /** Set on a Submittal pin placed from the Submittals tab: tapping it opens that submittal. */
  submittalId?: string;
  /** Set on a Detail pin: the sheet (same plan set) and box it points to, in that sheet's PDF points. */
  detail?: PinDetail;
  /** Punch items: who has to fix it (free text, e.g. a sub's name). */
  assignee?: string;
  /** Punch items: YYYY-MM-DD. */
  dueDate?: string;
  /** Set when status went to closed (cleared on reopen). */
  closedBy?: string;
  closedAt?: string;
  createdBy: string;
  /** Login id of the creator (foremen may change their own Photo and Punch pins). */
  createdById?: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
  version: number;
}

/** Where a Detail pin points: a box on another sheet of the same plan set. */
export interface PinDetail {
  pageId: string;
  /** [x, y, width, height] in the target sheet's PDF page points. */
  rect: [number, number, number, number];
  /** Optional callout, e.g. "5/C4.12". */
  label?: string;
}

export class PinError extends Error {
  constructor(message: string) { super(message); this.name = "PinError"; }
}

const s = (v: unknown, max: number) => (typeof v === "string" ? v.trim().slice(0, max) : "");
const pick = <T extends string>(v: unknown, list: readonly T[], fallback: T): T =>
  list.find((x) => typeof v === "string" && x.toLowerCase() === v.trim().toLowerCase()) ?? fallback;

/** Editable pin fields from client input (partial update over `base`). */
export function normalizePinInput(input: unknown, base?: Pin): Pick<Pin, "documentId" | "pageId" | "x" | "y" | "type" | "title" | "status" | "description" | "createdBy" | "detail" | "assignee" | "dueDate"> {
  const i = (input && typeof input === "object" ? input : {}) as Record<string, unknown>;
  const has = (k: string) => k in i;
  const num = (k: "x" | "y") => {
    if (!has(k)) { if (base) return base[k]; throw new PinError(`Pin ${k} is required.`); }
    const n = Number(i[k]);
    if (!Number.isFinite(n) || Math.abs(n) > 1e6) throw new PinError(`Pin ${k} must be a number.`);
    return Math.round(n * 100) / 100;
  };
  const documentId = has("documentId") ? s(i.documentId, 64) : base?.documentId ?? "";
  const pageId = has("pageId") ? s(i.pageId, 64) : base?.pageId ?? "";
  if (!documentId || !pageId) throw new PinError("A pin needs a plan set and sheet.");
  const type = has("type") ? pick(i.type, PIN_TYPES, "Note") : base?.type ?? "Note";
  const title = has("title") ? s(i.title, 200) : base?.title ?? "";
  const assignee = has("assignee") ? s(i.assignee, 120) : base?.assignee ?? "";
  const dueDate = has("dueDate") ? s(i.dueDate, 10) : base?.dueDate ?? "";
  if (dueDate && !/^\d{4}-\d{2}-\d{2}$/.test(dueDate)) throw new PinError("Due date must be YYYY-MM-DD.");
  const detail = has("detail") ? normalizeDetail(i.detail) : base?.detail;
  if (type === "Detail" && !detail) throw new PinError("A Detail pin needs a target sheet and a box around the detail.");
  return {
    documentId, pageId, x: num("x"), y: num("y"), type,
    title: title || type,
    status: has("status") ? pick(i.status, PIN_STATUSES, "open") : base?.status ?? "open",
    description: has("description") ? s(i.description, 5000) : base?.description ?? "",
    createdBy: base?.createdBy ?? (s(i.createdBy, 120) || "Web user"),
    ...(type === "Detail" && detail ? { detail } : {}),
    ...(assignee ? { assignee } : {}),
    ...(dueDate ? { dueDate } : {}),
  };
}

function normalizeDetail(v: unknown): PinDetail | undefined {
  if (!v || typeof v !== "object") return undefined;
  const d = v as Record<string, unknown>;
  const pageId = s(d.pageId, 64);
  const r = Array.isArray(d.rect) ? d.rect.map(Number) : [];
  if (!pageId || r.length !== 4 || !r.every((n) => Number.isFinite(n) && Math.abs(n) <= 1e6)) throw new PinError("The detail box is invalid.");
  if (r[2]! <= 0 || r[3]! <= 0) throw new PinError("Drag a box around the detail.");
  const label = s(d.label, 60);
  return { pageId, rect: r.map((n) => Math.round(n * 100) / 100) as PinDetail["rect"], ...(label ? { label } : {}) };
}
